
import { Injectable } from "@angular/core";
import { FilePondOptions } from "filepond";
import { loadFirebaseStorage } from "../firebase-storage/firebase-storage.service";
import { Store } from "@ngrx/store";
import { RootState, appActions, fromProgram } from "../../store";
import { ToastPosition, ToastService, ToastType } from "../toast/toast.service";
import { TranslateService } from "@ngx-translate/core";
import { MenuItem } from "../../interfaces";
import { EngagementFlowWithProgramEngagementFlowDto, Feature, ProgramIntegration } from "../api";
import { LINK_VARIABLES, MULTI_PASS } from "../../constants";
import { AbstractControl, FormArray, FormGroup } from "@angular/forms";
import { combineLatest, map, Observable } from "rxjs";
import type { EncodeOptions } from "@jsquash/webp/codec/enc/webp_enc";

@Injectable({ providedIn: "root" })
export class UtilsService {
  private readonly programIntegrations$: Observable<ProgramIntegration[]> = this.store.select(fromProgram.selectProgramIntegrations);
  private readonly engagementFlows$: Observable<EngagementFlowWithProgramEngagementFlowDto[]> = this.store.select(
    fromProgram.selectEngagements,
  );

  private readonly tiersEnabled$: Observable<boolean> = this.store.select(fromProgram.selectTiersEnabled);
  private readonly referralFlowActive$: Observable<boolean> = this.store.select(fromProgram.selectReferralFlowActive);
  private readonly shopifyPlus$: Observable<boolean> = this.store.select(fromProgram.selectShopifyPlus);

  private webpEncoder: ((data: ImageData, options?: Partial<EncodeOptions>) => Promise<ArrayBuffer>) | null = null;

  constructor(
    private readonly store: Store<RootState>,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
  ) {}

  public getFilePondConfig(): FilePondOptions | any {
    return {
      imagePreviewMaxHeight: 40,
      maxFileSize: "5MB",
      acceptedFileTypes: ["image/png", "image/jpeg", "image/webp"],
      imageTransformOutputStripImageHead: true,
      server: {
        process: async (fieldName: string, file: File, metadata: any, load: (snapshot: any) => void, error: (err: any) => void, progress: (isLoading: boolean, bytesTransferred: number, totalBytes: number) => void, _abort: () => void): Promise<void> => {
          this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: true }));
          try {
            const fileToUpload = await this.compressImage(file);
            const extension = fileToUpload.type.split("/")[1] ?? "bin";
            const { sdk, storage } = await loadFirebaseStorage();
            const storageRef = sdk.ref(storage, `files/${Date.now()}.${extension}`);
            const uploadTask = sdk.uploadBytesResumable(storageRef, fileToUpload);

            uploadTask.on(
              "state_changed",
              snapshot => {
                progress(true, snapshot.bytesTransferred, snapshot.totalBytes);
              },
              err => {
                this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: false }));
                error(err.message);
              },
              () => {
                this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: false }));
                sdk.getDownloadURL(uploadTask.snapshot.ref).then(downloadURL => {
                  load(downloadURL);
                });
              },
            );
          } catch (err: unknown) {
            this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: false }));
            error(err instanceof Error ? err.message : err);
          }
        },
        load: async (source: RequestInfo | URL, load: (blob: Blob) => void, error: (err: any) => void): Promise<void> => {
          try {
            // The bucket sends no `Vary: Origin`: a copy an <img> cached without CORS would fail this request
            const response = await fetch(source, { mode: "cors", cache: "reload" });
            if (!response.ok) {
              error(`HTTP ${response.status}`);
              return;
            }
            load(await response.blob());
          } catch (err) {
            error(err);
          }
        },
        revert: (uniqueFileId: string, load: () => void, _error: (err: any) => void): void => {
          load();
        },
      },
    };
  }

  public async createWalletBannerFromPoster(sourceUrl: string): Promise<string | null> {
    try {
      const response = await fetch(sourceUrl);
      if (!response.ok) return null;

      const bitmap = await createImageBitmap(await response.blob());
      const targetWidth = 1125;
      const targetHeight = 369;
      const targetRatio = targetWidth / targetHeight;
      const sourceRatio = bitmap.width / bitmap.height;
      const cropWidth = sourceRatio > targetRatio ? bitmap.height * targetRatio : bitmap.width;
      const cropHeight = sourceRatio > targetRatio ? bitmap.height : bitmap.width / targetRatio;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        bitmap.close();
        return null;
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.drawImage(
        bitmap,
        (bitmap.width - cropWidth) / 2,
        (bitmap.height - cropHeight) / 2,
        cropWidth,
        cropHeight,
        0,
        0,
        targetWidth,
        targetHeight,
      );
      bitmap.close();

      const croppedImage = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, "image/png"));
      if (!croppedImage) return null;

      const { sdk, storage } = await loadFirebaseStorage();
      this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: true }));
      const storageRef = sdk.ref(storage, `files/${Date.now()}.png`);
      const uploadTask = sdk.uploadBytesResumable(storageRef, croppedImage);

      return await new Promise<string | null>(resolve => {
        uploadTask.on(
          "state_changed",
          () => undefined,
          () => {
            this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: false }));
            resolve(null);
          },
          () => {
            sdk.getDownloadURL(uploadTask.snapshot.ref)
              .then(downloadUrl => resolve(downloadUrl))
              .catch(() => resolve(null))
              .finally(() => this.store.dispatch(appActions.setUploadImageLoading({ uploadImageLoading: false })));
          },
        );
      });
    } catch {
      return null;
    }
  }

  public copyToClipboard(
    value: string,
    props: {
      id?: string; title?: string; message?: string; showAsAbsolute?: boolean; position?: ToastPosition; type?: ToastType;
    } = { id: "#toastContainer" }): void {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        this.toast.show(
          {
            title: props.title || this.translate.instant("TOAST.copyToClipboard.success.title"),
            message: props.message || this.translate.instant("TOAST.copyToClipboard.success.message"),
            showAsAbsolute: props.showAsAbsolute,
            duration: 1500,
            position: props.position || "top-right",
            type: props.type || "info",
          },
          props.id,
        );
      })
      .catch(() => {
        this.toast.show(
          {
            title: this.translate.instant("TOAST.copyToClipboard.error.title"),
            message: this.translate.instant("TOAST.copyToClipboard.error.message"),
            showAsAbsolute: props.showAsAbsolute,
            type: "error",
          },
          props.id,
        );
      });
  }

  public formatDateToLocal(date: Date): string {
    const pad = (n: number): string | number => (n < 10 ? "0" + n : n);
    return (
      date.getFullYear() +
      "-" +
      pad(date.getMonth() + 1) +
      "-" +
      pad(date.getDate()) +
      "T" +
      pad(date.getHours()) +
      ":" +
      pad(date.getMinutes())
    );
  }

  // First day of the week for the user's locale, as 0 (Sunday) .. 6 (Saturday) to match Date.getDay()
  // and the stored `daysOfWeek` values. Uses the same browser locale the date pickers rely on.
  public getFirstWeekday(): number {
    const locale = new Intl.Locale(Intl.DateTimeFormat().resolvedOptions().locale) as Intl.Locale & {
      weekInfo?: { firstDay: number };
      getWeekInfo?: () => { firstDay: number };
    };

    // `weekInfo` is exposed as a method in some engines and a getter in others; default to Monday when unsupported.
    const firstDay = locale.getWeekInfo?.().firstDay ?? locale.weekInfo?.firstDay ?? 1;

    // Intl reports 1 (Monday) .. 7 (Sunday); convert to 0 (Sunday) .. 6 (Saturday).
    return firstDay % 7;
  }

  // Weekday values [0..6] reordered so the user's locale first day comes first (e.g. Monday first in Spain).
  public getOrderedWeekdays(): number[] {
    const firstWeekday = this.getFirstWeekday();
    return Array.from({ length: 7 }, (_, index) => (firstWeekday + index) % 7);
  }

  // Sort selected weekday values following the user's locale order instead of a fixed Sunday-first order.
  public sortWeekdays(days: number[]): number[] {
    const order = this.getOrderedWeekdays();
    return days.slice().sort((a, b) => order.indexOf(a) - order.indexOf(b));
  }

  public getProgramLinkVariables(): Observable<MenuItem[]> {
    return combineLatest([this.programIntegrations$, this.engagementFlows$, this.tiersEnabled$, this.referralFlowActive$, this.shopifyPlus$]).pipe(
      map(([programIntegrations, engagementFlows, tiersEnabled, referralFlowActive, shopifyPlus]) =>
        LINK_VARIABLES.reduce((variables, variable) => {
          if (!variable.integration && !variable.engagementFlow) {
            variables.push({
              ...variable,
              children: variable.children?.map(child => {
                if (child.title === "linkCustomerAppTiers") return { ...child, disabled: !tiersEnabled };
                if (child.title === "linkCustomerAppReferrals") return { ...child, disabled: !referralFlowActive };
                return child;
              }),
            });
          } else {
            programIntegrations?.forEach(programIntegration => {
              if (programIntegration.integration.name === variable.integration) {
                const multiPassSecret = (programIntegration.properties as unknown as { multiPassSecret: boolean })?.multiPassSecret;
                // Multipass is a Shopify Plus feature: offered only on Plus, or where it is already set up
                if (variable.title === MULTI_PASS && (shopifyPlus || multiPassSecret)) {
                  variables.push({ ...variable, disabled: !multiPassSecret });
                }
              }
            });

            engagementFlows.forEach(engagementFlow => {
              if (
                engagementFlow.name === variable.engagementFlow &&
                engagementFlow.programEngagementFlows.length
              ) {
                variables.push({ ...variable, disabled: !engagementFlow.programEngagementFlows[0].active });
              }
            });
          }

          return variables;
        }, [] as MenuItem[]),
      ),
    );
  }

  public isFeatureEnabled(programIntegration: ProgramIntegration, features: Feature[], featureName: string): boolean {
    return programIntegration?.enabledFeatures
      ?.map(enabledFeatureId => features.find(feature => feature.id === enabledFeatureId)?.name)
      .some(name => name === featureName);
  }

  private async compressImage(file: File): Promise<File> {
    switch (file.type) {
      case "image/png":
        return this.compressPng(file);
      case "image/webp":
        return this.compressWebp(file);
      default:
        return file;
    }
  }

  private async compressPng(file: File): Promise<File> {
    const upng = await import("upng-js");
    const UPNG = upng.default ?? upng;
    const buffer = await file.arrayBuffer();
    const decoded = UPNG.decode(buffer);
    const rgba = UPNG.toRGBA8(decoded);
    const compressed = UPNG.encode(rgba, decoded.width, decoded.height, 256);

    return new File([compressed], file.name, { type: "image/png" });
  }

  private async compressWebp(file: File): Promise<File> {
    if (!this.webpEncoder) {
      const { default: encode, init } = await import("@jsquash/webp/encode");
      await init({ locateFile: (file: string) => `assets/wasm/webp/${file}` });
      this.webpEncoder = encode;
    }

    const imageData = await this.fileToImageData(file);
    const compressed = await this.webpEncoder(imageData, { quality: 98 });

    return new File([compressed], file.name, { type: "image/webp" });
  }

  private async fileToImageData(file: File): Promise<ImageData> {
    const bitmap = await createImageBitmap(file);
    const canvas = new OffscreenCanvas(bitmap.width, bitmap.height);
    const ctx = canvas.getContext("2d");

    ctx.drawImage(bitmap, 0, 0);
    bitmap.close();

    return ctx.getImageData(0, 0, canvas.width, canvas.height);
  }

  public validateForm(form: FormGroup): void {
    this.markControlForValidation(form);
  }

  private markControlForValidation(control: AbstractControl): void {
    control.markAsDirty();
    control.markAsTouched();

    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.values(control.controls).forEach(childControl => this.markControlForValidation(childControl));
    }

    control.updateValueAndValidity({ onlySelf: true });
  }
}
